import { Grid, makeStyles } from '@material-ui/core'
import DazzedHeadingForGrid from 'components/common/text/DazzedHeadingForGrid'
import { NEEDS_ATTENTION_REASON_ORDER } from 'consts/giConsts'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PatientListTitle from '../../common/PatientsList/PatientListTitle'
import ReasonWithCount from './ReasonWithCount'
import { trackEvent } from 'utils/analyticsUtils'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    paddingLeft: 5,
    paddingBottom: 10
  },
  toggleButton: {
    margin: '2px 3px',
    height: 37,
    color: '#001335',
    backgroundColor: '#E7EAFF',
    borderRadius: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 10px',
    cursor: 'pointer'
  },
  allReasonsButton: {
    width: 96,
    // Tracks the chip block rather than assuming a row count: the seven chips wrap to two rows
    // from about 1600px and to three at 1440px, and one row needs a column wider than ~2400px.
    // 84px is the smallest box that holds "7" over "Total".
    //
    // height rather than alignSelf: this sits inside a Grid item, which MUI renders as a block,
    // so alignSelf has nothing to resolve against. The Grid item itself stretches to the row as
    // a flex child of the outer container, and 100% makes the button fill it.
    height: '100%',
    minHeight: 84,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // Centered rather than space-between: when the chip block is three rows tall the tile
    // stretches with it, and the number and its label stay together instead of drifting apart.
    justifyContent: 'center',
    gap: 4,
    backgroundColor: ({ allReasonsSelected }) => (allReasonsSelected ? '#7586FF' : '#E7EAFF')
  },
  // Design review: the total tile and the reason chips share one row. The chips take the width left
  // beside the tile and wrap inside it, instead of the whole chip block dropping under the tile.
  // The tile stretches to the chip block once it is taller than the tile's minimum (three rows).
  headerRow: {
    flexWrap: 'nowrap',
    alignItems: 'stretch'
  },
  // A chip row is 41px (37 + 2px margins), so one or two rows are shorter than the tile's 84px
  // minimum and are centered against it; three rows stretch the tile instead.
  reasonsItem: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center'
  },
  reasonsContainer: {
    // Wraps on its own; no fixed height, so adding a reason reflows instead of overhanging.
    flexWrap: 'wrap'
  },
  allReasonsLabelsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  reasonLabelContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
}))

const NeedsAttentionHeader = ({ total, countsByReasons, selectedReasons, setSelectedReasons }) => {
  const { t } = useTranslation()

  const [allReasonsSelected, setAllReasonsSelected] = useState(true)

  const classes = useStyles({ allReasonsSelected })

  const handleReasonClicked = useCallback(
    reason => {
      let newSelectedReasons = []

      if (selectedReasons.includes(reason)) {
        newSelectedReasons = selectedReasons.filter(r => r !== reason)
      } else {
        newSelectedReasons = [...selectedReasons, reason]
      }

      trackEvent('Copilot Control - Needs attention header - Reason clicked', {
        selectedReasons: newSelectedReasons
      })
      setSelectedReasons(newSelectedReasons)
    },
    [selectedReasons, setSelectedReasons]
  )

  useEffect(() => {
    if (selectedReasons.length === 0) {
      setAllReasonsSelected(true)
    } else {
      setAllReasonsSelected(false)
    }
  }, [selectedReasons])

  return (
    <Grid container className={classes.root}>
      <PatientListTitle />
      <Grid container className={classes.headerRow}>
        <Grid item>
          <Grid
            className={[classes.toggleButton, classes.allReasonsButton].join(' ')}
            style={{
              border: allReasonsSelected ? '2px solid #3C52EF' : 'none'
            }}
            onClick={() => setSelectedReasons([])}
          >
            <DazzedHeadingForGrid
              style={{
                color: allReasonsSelected ? '#FFFFFF' : '#001335'
              }}
              semibold
              xs={40}
            >
              {total}
            </DazzedHeadingForGrid>
            <DazzedHeadingForGrid
              style={{
                color: allReasonsSelected ? '#FFFFFF' : '#001335'
              }}
              semibold
              xs={10}
            >
              {t('pages.copilotControl.widgets.needsAttention.totalLabel')}
            </DazzedHeadingForGrid>
          </Grid>
        </Grid>
        <Grid item className={classes.reasonsItem}>
          <Grid container className={classes.reasonsContainer}>
            {NEEDS_ATTENTION_REASON_ORDER.map(reason => (
              <ReasonWithCount
                key={reason}
                containerClassName={classes.toggleButton}
                reason={reason}
                count={countsByReasons[reason]}
                onReasonClicked={handleReasonClicked}
                selected={selectedReasons.includes(reason)}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default NeedsAttentionHeader
